import { useEffect, useRef, useState } from 'react'

import { mentionToken, parseBody } from '../lib/mentions'
import { kindOfId, KINDS, mentionClass, type EntityKind } from '../lib/model'
import { Icon, iconName } from './Icon'
import { useMentionScope } from './Mentions'

interface Match {
  id: string
  title: string
  kind: EntityKind
}

interface Popup {
  items: Match[]
  active: number
  start: number // offset of '@' within the anchor text node
  end: number // caret offset within the anchor text node
  node: Text
  rect: { left: number; top: number }
  query: string // the current @query, so re-detection can preserve the active index
}

function buildInitialDom(el: HTMLElement, value: string) {
  el.replaceChildren()
  for (const seg of parseBody(value)) {
    if (seg.type === 'mention') {
      el.appendChild(makeChip(seg.id, seg.label))
    } else {
      const lines = seg.text.split('\n')
      lines.forEach((line, i) => {
        if (i > 0) el.appendChild(document.createElement('br'))
        if (line) el.appendChild(document.createTextNode(line))
      })
    }
  }
}

function makeChip(id: string, label: string): HTMLElement {
  const span = document.createElement('span')
  const kind = kindOfId(id)
  // Same colour code as the rest of the app, so the editor previews what the saved
  // body will look like.
  span.className = mentionClass(kind ?? undefined)
  span.contentEditable = 'false'
  span.dataset.id = id
  span.dataset.label = label
  span.textContent = label
  return span
}

/**
 * What a paste should become.
 *
 * With no handler at all the browser drops the source page's HTML straight into the
 * editor: `<span style="font-family: Times">`, `<a href>`, `<ul><li>`. The serializer
 * below then keeps the text and throws the tags away, so pasting a link LOST its URL
 * and pasting a list ran the items together — "one" and "two" arriving as `onetwo`.
 * Both were silent.
 *
 * So the paste is converted to the markdown this app renders, which is the format the
 * body is stored in anyway. Modest on purpose: links, lists, emphasis, code, headings
 * and block breaks. Anything else contributes its text and nothing more.
 */
function htmlToMarkdown(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const out: string[] = []

  const inline = (node: Node): string => {
    let text = ''
    node.childNodes.forEach((c) => {
      if (c.nodeType === Node.TEXT_NODE) text += (c as Text).data.replace(/\s+/g, ' ')
      else if (c.nodeType === Node.ELEMENT_NODE) text += wrap(c as HTMLElement)
    })
    return text
  }

  const wrap = (el: HTMLElement): string => {
    const inner = inline(el)
    switch (el.tagName) {
      case 'A': {
        const href = el.getAttribute('href') ?? ''
        // Same rule the renderer applies: only a scheme that navigates, and never a
        // relative path, which would point at crum rather than at the source.
        return /^(https?:\/\/|mailto:)/i.test(href) ? `[${inner || href}](${href})` : inner
      }
      case 'STRONG':
      case 'B':
        return inner.trim() ? `**${inner.trim()}**` : ''
      case 'EM':
      case 'I':
        return inner.trim() ? `*${inner.trim()}*` : ''
      case 'CODE':
        return inner.trim() ? `\`${inner.trim()}\`` : ''
      case 'BR':
        return '\n'
      default:
        return inner
    }
  }

  const block = (el: HTMLElement) => {
    const tag = el.tagName
    if (tag === 'UL' || tag === 'OL') {
      let n = 1
      el.querySelectorAll(':scope > li').forEach((li) => {
        const text = inline(li).trim()
        if (text) out.push(tag === 'OL' ? `${n++}. ${text}` : `- ${text}`)
      })
      return
    }
    if (/^H[1-6]$/.test(tag)) {
      const text = inline(el).trim()
      if (text) out.push(`${'#'.repeat(Math.min(3, Number(tag[1])))} ${text}`)
      return
    }
    if (tag === 'PRE') {
      out.push('```', el.textContent ?? '', '```')
      return
    }
    if (tag === 'BLOCKQUOTE') {
      const text = inline(el).trim()
      if (text) out.push(`> ${text}`)
      return
    }
    if (tag === 'DIV' || tag === 'P' || tag === 'SECTION' || tag === 'ARTICLE') {
      // A wrapper whose children are themselves blocks contributes nothing itself.
      const blocks = [...el.children].filter((c) =>
        /^(UL|OL|H[1-6]|PRE|BLOCKQUOTE|DIV|P|SECTION|ARTICLE)$/.test(c.tagName),
      )
      if (blocks.length > 0) {
        blocks.forEach((c) => block(c as HTMLElement))
        return
      }
      const text = inline(el).trim()
      if (text) out.push(text)
      return
    }
    const text = wrap(el).trim()
    if (text) out.push(text)
  }

  doc.body.childNodes.forEach((n) => {
    if (n.nodeType === Node.TEXT_NODE) {
      const t = (n as Text).data.trim()
      if (t) out.push(t)
    } else if (n.nodeType === Node.ELEMENT_NODE) block(n as HTMLElement)
  })

  return out
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function serialize(root: HTMLElement): string {
  let out = ''
  const walk = (node: Node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += (child as Text).data
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement
        if (el.classList.contains('mention')) {
          out += mentionToken(el.dataset.id ?? '', el.dataset.label ?? '')
        } else if (el.tagName === 'BR') {
          out += '\n'
        } else {
          if (/^(DIV|P)$/.test(el.tagName) && out && !out.endsWith('\n')) out += '\n'
          walk(el)
        }
      }
    })
  }
  walk(root)
  return out.replace(/\n+$/, '')
}

export function MentionInput({
  value,
  onChange,
  placeholder,
  submitOnEnter = false,
  onSubmit,
  autoFocus = false,
  minHeight = 40,
}: {
  value: string
  onChange: (next: string) => void
  placeholder?: string | undefined
  submitOnEnter?: boolean
  onSubmit?: () => void
  autoFocus?: boolean
  minHeight?: number
}) {
  // What `@` may reach comes from the scope, so the same editor serves the team
  // (the whole log) and a client (their own page). See components/Mentions.tsx.
  const scope = useMentionScope()
  const ref = useRef<HTMLDivElement>(null)
  const [popup, setPopup] = useState<Popup | null>(null)

  // Seed the DOM once from the incoming value (uncontrolled thereafter; the
  // parent resets by remounting with a new key).
  useEffect(() => {
    if (ref.current) {
      buildInitialDom(ref.current, value)
      if (autoFocus) ref.current.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function search(query: string): Match[] {
    return scope.search(query)
  }

  function detectTrigger() {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return setPopup(null)
    const node = sel.anchorNode
    if (!node || node.nodeType !== Node.TEXT_NODE || !ref.current?.contains(node))
      return setPopup(null)
    const text = (node as Text).data
    const before = text.slice(0, sel.anchorOffset)
    const m = /(?:^|\s)@([^\s@]*)$/.exec(before)
    if (!m) return setPopup(null)
    const query = m[1] ?? ''
    const items = search(query)
    const range = sel.getRangeAt(0).cloneRange()
    const rect = range.getBoundingClientRect()
    // Preserve the highlighted item while navigating: re-detection fires on every
    // keyup (arrows included), so only reset to the top when the query changed.
    setPopup((prev) => ({
      items,
      active:
        prev && prev.query === query ? Math.min(prev.active, Math.max(0, items.length - 1)) : 0,
      start: sel.anchorOffset - query.length - 1, // the '@'
      end: sel.anchorOffset,
      node: node as Text,
      rect: { left: rect.left, top: rect.bottom },
      query,
    }))
  }

  function choose(match: Match) {
    if (!popup) return
    const { node, start, end } = popup
    const range = document.createRange()
    range.setStart(node, start)
    range.setEnd(node, end)
    range.deleteContents()
    const chip = makeChip(match.id, match.title)
    range.insertNode(chip)
    const space = document.createTextNode(' ')
    chip.after(space)
    const after = document.createRange()
    after.setStartAfter(space)
    after.collapse(true)
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(after)
    setPopup(null)
    if (ref.current) onChange(serialize(ref.current))
  }

  /**
   * Put plain text in at the caret.
   *
   * `insertText` rather than DOM surgery, because it is the one insertion the browser
   * keeps on its own undo stack. It does NOT honour a newline inside the string,
   * though — a pasted list arrived as `- one- two` — so the lines go in one at a time
   * with a real line break between them.
   */
  function insert(text: string) {
    const lines = text.split('\n')
    lines.forEach((line, i) => {
      if (i > 0) document.execCommand('insertLineBreak')
      if (line) document.execCommand('insertText', false, line)
    })
    if (ref.current) onChange(serialize(ref.current))
  }

  function onPaste(e: React.ClipboardEvent) {
    const html = e.clipboardData.getData('text/html')
    const plain = e.clipboardData.getData('text/plain')
    if (!html && !plain) return
    e.preventDefault()
    insert(html ? htmlToMarkdown(html) || plain : plain)
    detectTrigger()
  }

  /**
   * ⌘B and ⌘I write the markdown rather than letting the browser style the div.
   *
   * Left alone, the browser inserts `<b>`, the editor shows real bold, and the
   * serializer drops the tag on the way out — so the formatting was visibly applied
   * and silently lost at save. Wrapping the selection means the shortcut does what it
   * looks like it does, in the notation the body is actually stored in.
   */
  function emphasise(marks: string) {
    const sel = window.getSelection()
    const text = sel && !sel.isCollapsed ? sel.toString() : ''
    insert(`${marks}${text}${marks}`)
    if (!text) {
      // Land the caret between the marks, ready to type.
      const s2 = window.getSelection()
      if (s2 && s2.rangeCount > 0) {
        const r = s2.getRangeAt(0)
        r.setStart(r.startContainer, Math.max(0, r.startOffset - marks.length))
        r.collapse(true)
        s2.removeAllRanges()
        s2.addRange(r)
      }
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && !e.altKey) {
      const k = e.key.toLowerCase()
      if (k === 'b' || k === 'i') {
        e.preventDefault()
        emphasise(k === 'b' ? '**' : '*')
        return
      }
    }

    if (popup && popup.items.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setPopup({ ...popup, active: (popup.active + 1) % popup.items.length })
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setPopup({ ...popup, active: (popup.active - 1 + popup.items.length) % popup.items.length })
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        choose(popup.items[popup.active]!)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setPopup(null)
        return
      }
    }
    if (submitOnEnter && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSubmit?.()
    }
  }

  return (
    <div className="relative">
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onInput={() => {
          if (ref.current) onChange(serialize(ref.current))
          detectTrigger()
        }}
        onKeyUp={detectTrigger}
        onClick={detectTrigger}
        onBlur={() => setTimeout(() => setPopup(null), 150)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        className="mention-input rounded-control text-body w-full border px-3 py-2 leading-relaxed outline-none focus:ring-2"
        style={
          {
            minHeight,
            borderColor: 'var(--border)',
            background: 'var(--panel)',
            '--tw-ring-color': 'color-mix(in srgb, var(--accent) 40%, transparent)',
          } as React.CSSProperties
        }
      />
      {popup && (
        <ul
          className="rounded-control fixed z-50 max-h-64 w-64 overflow-auto border py-1 shadow-lg"
          style={{
            left: popup.rect.left,
            top: popup.rect.top + 4,
            background: 'var(--panel)',
            borderColor: 'var(--border)',
          }}
        >
          {popup.items.length === 0 && (
            <li className="text-body px-3 py-2" style={{ color: 'var(--muted)' }}>
              No matches
            </li>
          )}
          {popup.items.map((m, i) => (
            <li key={m.id}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault()
                  choose(m)
                }}
                className="text-body flex w-full items-center gap-2 px-3 py-1.5 text-left"
                style={{ background: i === popup.active ? 'var(--hover)' : 'transparent' }}
              >
                <Icon
                  name={iconName(KINDS[m.kind].icon)}
                  size={14}
                  style={{ color: 'var(--muted)' }}
                />
                <span className="truncate">{m.title}</span>
                <span className="text-meta ml-auto" style={{ color: 'var(--muted)' }}>
                  {KINDS[m.kind].singular}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* Nothing on screen said markdown worked, so nobody used it. One line, in the
          quietest type the app has, naming the three things worth knowing. */}
      <p className="meta mt-1" style={{ fontSize: 'var(--text-label)' }}>
        <b>@</b> to mention · <b>⌘B</b> for <b>**bold**</b> · markdown renders
      </p>
    </div>
  )
}
